import { Mail, Phone, MapPin, Headphones } from "lucide-react";
import { motion } from "framer-motion";
import "./index.css";
import Navbar from "./components/Navbar";
import HeroFeatures from "./components/HeroFeatures";
import OurServices from "./components/OurServices";
import Pricing from "./components/Pricing";
import Testimonials from "./components/Testimonials";
import FAQ from "./components/FAQ";
import BrandLogo from "./components/BrandLogo";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14, delayChildren: 0.15 } },
};

const cardIn = {
  hidden: { opacity: 0, y: 32 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

function App() {
  return (
    <div className="page">
      <header id="home" className="hero-front">
        <Navbar />

        <section className="hero">
          <img
            src="/hero-background.png"
            alt=""
            className="hero-background-image"
            aria-hidden="true"
          />
          <div className="hero-overlay" aria-hidden="true" />

          <div className="hero-content">
              <motion.div
                className="hero-text"
                initial="hidden"
                animate="visible"
                variants={stagger}
              >
                <motion.h1 className="hero-title" variants={fadeUp}>
                  <span className="hero-title-line">
                    <span className="hero-title-line--accent">Professional</span> Drivers
                  </span>
                  <span className="hero-title-line hero-title-line--accent">Anytime</span>
                </motion.h1>

                <motion.p className="hero-subtitle" variants={fadeUp}>
                  Book verified and trusted drivers for your personal vehicle
                  anytime, anywhere.
                </motion.p>

                <motion.div className="hero-buttons" variants={fadeUp}>
                  <a href="#get-app" className="primary hero-btn">
                    Book a Driver
                  </a>
                </motion.div>
              </motion.div>
            </div>

            <HeroFeatures />
          </section>
        </header>

        <main className="site-sections">
      <OurServices />

      <section id="about" className="section features-section">
        <div className="why-wrapper">
          <div className="why-top">
            <motion.div
              className="why-left"
              initial={{ opacity: 0, x: -28 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="why-badge">★ Trusted by Thousands</span>

              <h2>Why Choose iDRIVEA?</h2>

              <p>
                We combine technology with trust to deliver the safest and most
                reliable driver services.
              </p>
            </motion.div>

            <div className="why-right">
              <div className="why-cards">
                <motion.div
                  className="why-stat-card"
                  custom={0}
                  variants={cardIn}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.4 }}
                  whileHover={{ y: -8, scale: 1.03 }}
                >
                  <div className="round-icon">👥</div>
                  <h3>5k+</h3>
                  <h4>Happy Customers</h4>
                  <p>Thousands of satisfied customers across cities.</p>
                </motion.div>

                <motion.div
                  className="why-stat-card"
                  custom={1}
                  variants={cardIn}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.4 }}
                  whileHover={{ y: -8, scale: 1.03 }}
                >
                  <div className="round-icon">⭐</div>
                  <h3>4.9/5</h3>
                  <h4>Customer Rating</h4>
                  <p>Top-rated by our amazing customers.</p>
                </motion.div>
              </div>
            </div>
          </div>

          <div className="bottom-stats">
            {[
              { icon: "fas fa-shield-alt", value: "99%", title: "Verified Drivers", text: "Rigorous verification for your safety." },
              { icon: "fas fa-check-circle", value: "98%", title: "Safety Score", text: "Committed to providing a safe experience." },
              { icon: "fas fa-map-marker-alt", value: "97%", title: "GPS Accuracy", text: "Real-time tracking with high accuracy." },
              { icon: "fas fa-star", value: "4.9/5", title: "Customer Rating", text: "Loved by thousands of happy customers." },
            ].map((stat, i) => (
              <motion.div
                className="bottom-card"
                key={stat.title}
                custom={i}
                variants={cardIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
                whileHover={{ y: -4 }}
              >
                <div className="bottom-icon-fa">
                  <i className={stat.icon}></i>
                </div>
                <div>
                  <h3>{stat.value}</h3>
                  <h4>{stat.title}</h4>
                  <p>{stat.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Pricing />
      <Testimonials />
      <FAQ />

      <section id="get-app" className="section get-app-section">
        <div className="get-app-inner">
          <motion.div
            className="get-app-text"
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
          >
            <h2>Book a Driver</h2>
            <p>Download iDRIVEA and book trusted drivers anytime, anywhere.</p>

            <div className="store-buttons">
              <a className="store-btn appstore" href="https://apps.apple.com/app/idriver" target="_blank" rel="noopener noreferrer">
                Download on the App Store
              </a>

              <a className="store-btn playstore" href="https://play.google.com/store/apps/details?id=com.idriver" target="_blank" rel="noopener noreferrer">
                Get it on Google Play
              </a>
            </div>
          </motion.div>

          <motion.div
            className="get-app-mockup"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <BrandLogo size="lg" className="brand-logo-img--float" />
          </motion.div>
        </div>
      </section>

      <footer id="contact" className="site-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <a href="#home" className="footer-logo">
              <BrandLogo variant="image" surface="light" size="footer" />
            </a>
            <p className="footer-tagline">
              Trusted drivers on demand — instant booking, verified professionals,
              secure payments.
            </p>
          </div>

          <div className="footer-contact-block">
            <h4 className="footer-heading">Contact Us</h4>
            <div className="footer-contact-grid">
              <div className="footer-contact-item">
                <span className="footer-contact-icon" aria-hidden="true">
                  <Mail size={18} strokeWidth={2} />
                </span>
                <div className="footer-contact-body">
                  <span className="footer-contact-label">Email</span>
                  <a href="mailto:info@idrivea.com">info@idrivea.com</a>
                </div>
              </div>

              <div className="footer-contact-item">
                <span className="footer-contact-icon" aria-hidden="true">
                  <Phone size={18} strokeWidth={2} />
                </span>
                <div className="footer-contact-body">
                  <span className="footer-contact-label">Phone</span>
                  <a href="tel:+919916990011">9916990011</a>
                </div>
              </div>

              <div className="footer-contact-item">
                <span className="footer-contact-icon" aria-hidden="true">
                  <MapPin size={18} strokeWidth={2} />
                </span>
                <div className="footer-contact-body">
                  <span className="footer-contact-label">Address</span>
                  <span>Bangalore, Karnataka, India</span>
                </div>
              </div>

              <div className="footer-contact-item">
                <span className="footer-contact-icon" aria-hidden="true">
                  <Headphones size={18} strokeWidth={2} />
                </span>
                <div className="footer-contact-body">
                  <span className="footer-contact-label">Support</span>
                  <span>24/7 Customer Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-credit">Powered by Mumble Technology</p>
        </div>
      </footer>
        </main>
    </div>
  );
}

export default App;
